import { AnonymousListItem } from "@/components/employee/AnonymousListItem.tsx";
import { FormParticipantStatusTag } from "@/components/forms/components/FormParticipantStatusTag.tsx";
import { useModals, Box, Button, Stack, Tag, Title } from "@skailer-com/uikit";
import { ScoreTag } from "@/pages/talent/performance-reviews/view/components/ScoreTag.tsx";
import { PerformanceReviewUnsolicitedTag } from "@/pages/talent/performance-reviews/view/components/PerformanceReviewUnsolicitedTag.tsx";
import { FormResultForReviewerModal } from "@/pages/talent/performance-reviews/view/widgets/FormResultForReviewerModal.tsx";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ApiFormParticipantStatus } from "@/types/forms/form.types.ts";
import {
  ApiPerformanceReviewReviewerTypeSummary,
  PerformanceReviewReviewerTypeItems,
} from "@/types/performance-review.types.ts";
import { useTranslation } from "react-i18next";

export const PerformanceReviewReviewerTypeSummaries = ({
  reviewerTypeSummaries,
}: {
  reviewerTypeSummaries: ApiPerformanceReviewReviewerTypeSummary[];
}) => {
  const { t } = useTranslation();
  const { openModal } = useModals();

  const handleViewResults = (
    reviewerTypeId: number,
    respondentUuid: string | null,
    employeeId?: number
  ) => {
    if (!respondentUuid) return;

    openModal(FormResultForReviewerModal, {
      reviewerTypeId: reviewerTypeId,
      respondentUuid: respondentUuid,
      employeeId: employeeId,
    });

    analyticsService.trackEvent(analyticEvents.performanceReviews.reviewerResultViewed, {
      [analyticProperties.id]: respondentUuid,
    });
  };

  return (
    <Stack gap={"2xl"}>
      {reviewerTypeSummaries.map((type) => {
        const reviewerType = type.reviewer_type;
        if (type.reviewers_summaries.length === 0) {
          return;
        }

        return (
          <Box key={`reviewer-type-${reviewerType.id}`}>
            <Title
              paddingBottom={true}
              header={
                (PerformanceReviewReviewerTypeItems().find((item) => item.id == reviewerType.type)
                  ?.shortTitle ??
                  reviewerType.title ??
                  "") + (type.summary.total_count > 1 ? ` (${type.summary.total_count})` : "")
              }
              caption={
                type.summary.completed_count > 0 &&
                type.summary.total_count > 1 &&
                type.summary.rating.avg_scale_score
                  ? t("performance_review:average_score_based_on_responses", {
                      score: type.summary.rating.avg_scale_score.toFixed(1),
                      count: type.summary.completed_count,
                    })
                  : ""
              }
            />
            <Stack>
              {type.reviewers_summaries.map((reviewer, index) => {
                const respondentUuid = reviewer.respondent_uuid;
                const canViewResults =
                  !type.is_below_anonymity_threshold &&
                  reviewer.status === ApiFormParticipantStatus.completed &&
                  respondentUuid &&
                  reviewer.employee;

                return (
                  <AnonymousListItem
                    employee={reviewer.employee}
                    key={`reviewer-item-${reviewerType.id}-${reviewer.id ?? reviewer.respondent_uuid ?? index}`}
                    uuid={respondentUuid}
                    index={index}
                    valueSlot={
                      <Stack direction={"horizontal"} gap="sm" items="center">
                        {reviewer.is_unsolicited === true && <PerformanceReviewUnsolicitedTag />}
                        {reviewer.status ? (
                          <>
                            {reviewer.status === ApiFormParticipantStatus.completed ? (
                              <Stack gap={"md"} direction={"horizontal"} items={"center"}>
                                <ScoreTag score={reviewer.rating.avg_scale_score} />
                                {canViewResults && (
                                  <Button
                                    variant={"secondary"}
                                    onClick={() =>
                                      handleViewResults(
                                        reviewerType.id,
                                        respondentUuid,
                                        reviewer.employee?.id
                                      )
                                    }
                                  >
                                    {t("common:view")}
                                  </Button>
                                )}
                              </Stack>
                            ) : (
                              <FormParticipantStatusTag status={reviewer.status} />
                            )}
                          </>
                        ) : (
                          <Tag>{t("common:anonymous")}</Tag>
                        )}
                      </Stack>
                    }
                  />
                );
              })}
            </Stack>
          </Box>
        );
      })}
    </Stack>
  );
};
